import { Input } from '@cokitchen/cokitchen-components';
import SelectDropdown from 'components/base/select-dropdown';
import useDashboardContext from 'contexts/dashboard.context';
import { RequestItemType } from 'models/item-request.model';
import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from 'services/base.service';
import { ReactComponent as DotIcon } from 'assets/icons/dot-icon.svg';
import { DeleteIcon } from 'cokitchen-icons';

export interface SendRequesttItemType {
  batch_no: string;
  quantity_sent: number;
  id: string;
}
type BatchOptionsType = { id: string; value: string; name: string; price: string }[];
const KitchenRequestItem = ({
  setItemRequestToEdit,
  requestItem
}: {
  setItemRequestToEdit: (
    arg0: {
      kitchen_request_item_id: string;
      quantity_sent: number;
      batch_no: number;
      batch_id: string;
    }[],
    itemId?: string
  ) => void;
  requestItem: RequestItemType;
}) => {
  const { activeStore } = useDashboardContext();
  const [batches, setBatches] = useState<BatchOptionsType>([]);
  const [availableBatches, setAvailableBatches] = useState<BatchOptionsType>([]);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const { ingredient, id } = requestItem;
  const [selectedBatches, setSelectedBatches] = useState([
    { quantity_sent: 0, batch_no: '0', id: '' }
  ]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const aBatches = batches.filter((batch) =>
      selectedBatches.every((item) => item.id !== batch.id)
    );
    setAvailableBatches(aBatches);
  }, [batches, selectedBatches]);

  const getIngredientBatches = async () => {
    setLoading(true);
    try {
      const res: { data: { data: any } } = await apiService.get(
        `ingredients/${ingredient.id}/stores/${activeStore?.id}`,
        {
          version: 'v2'
        }
      );
      setBatches(
        res.data.data.main_batches?.map((res: any) => ({
          value: res.id,
          name: String(res.batch_no),
          id: res.id,
          price: `${res.quantity.toString()}`
        }))
      );
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getIngredientBatches();
  }, []);

  useEffect(() => {
    if (batches.length === 0) return;
    setSelectedBatches([
      {
        quantity_sent: 0,
        id: batches[0]?.id,
        batch_no: String(batches[0]?.name)
      }
    ]);
  }, [batches]);

  const addNewBatch = () => {
    if (availableBatches.length === 0) return;

    setSelectedBatches((prev) => [
      ...prev,
      { quantity_sent: 0, id: availableBatches[0].id, batch_no: String(availableBatches[0].name) }
    ]);

    setAvailableBatches((prev) =>
      prev.filter((batch) => batch.value !== availableBatches[0].value)
    );
  };

  const updateSentItems = (index: number, updates: Partial<SendRequesttItemType>) => {
    const updatedItems = [...selectedBatches];
    updatedItems[index] = {
      ...updatedItems[index],
      ...updates
    };
    setSelectedBatches(updatedItems);

    setItemRequestToEdit(
      updatedItems.map((item) => ({
        kitchen_request_item_id: id,
        quantity_sent: Number(item.quantity_sent),
        batch_no: Number(item.batch_no),
        batch_id: item.id
      })),
      id
    );
  };

  const removeBatch = (index: number) => {
    const updatedItems = selectedBatches.filter((_, idx) => idx !== index);
    setSelectedBatches(updatedItems);
    setErrors({});
    setItemRequestToEdit(
      updatedItems.map((item) => ({
        kitchen_request_item_id: id,
        quantity_sent: Number(item.quantity_sent),
        batch_no: Number(item.batch_no),
        batch_id: item.id
      })),
      id
    );
  };
  const totalQuantitySent = useMemo(
    () =>
      selectedBatches.reduce((acc, cur) => acc + cur.quantity_sent, 0) +
      requestItem.quantity_sent +
      ' ' +
      (requestItem.ingredient.unit_type ?? 'gram') +
      ' ' +
      'sent',
    [selectedBatches]
  );
  const updateQuantitySentFromBatch = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    maxQuantity: string
  ) => {
    const val = Number(e.target.value);
    if (val > Number(maxQuantity)) {
      setErrors((prev) => ({
        ...prev,
        [index]: `Quantity sent cannot be more than the value of the ${maxQuantity}.`
      }));
      return;
    }

    const currentTotal = selectedBatches.reduce(
      (acc, curr, idx) => (idx === index ? acc : acc + curr.quantity_sent),
      0
    );

    if (currentTotal + val + (requestItem.quantity_sent || 0) > requestItem.quantity) {
      setErrors((prev) => ({
        ...prev,
        [index]: 'Total quantity sent cannot exceed requested quantity.'
      }));
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
    updateSentItems(index, { quantity_sent: val });
  };

  return (
    <div className='px-[28px] mb-10'>
      <div
        className={`font-medium mb-2  ${batches?.length === 0 && 'opacity-50 cursor-not-allowed'}`}>
        <p>{requestItem?.ingredient.name}</p>

        <div className='flex gap-2 items-center text-sm '>
          <p>{requestItem.quantity} requested</p>
          <DotIcon className='bg-[#D9D9D9]' />
          <p className='text-blue-5'>{totalQuantitySent}</p>
        </div>
      </div>
      <div className='text-sm gap-x-[28px] gap-y-6  border-b border-[#EDEDEE] pb-2 mt-4'>
        {loading ? (
          <div className='flex justify-center items-center w-full h-full'>
            <p>Loading </p>
          </div>
        ) : (
          selectedBatches.map((b: any, index: number) => {
            const currentBatch = batches.find((batch) => batch.id === b.id);
            const maxQuantity = currentBatch?.price || '';
            return (
              <div
                key={index}
                className={`flex gap-4 mt-4  ${
                  batches?.length === 0 && 'opacity-50 cursor-not-allowed'
                }`}>
                <Input
                  design='bordered'
                  label='Quantity sent per batch'
                  placeholder='0'
                  className='w-full'
                  name='quantity'
                  value={b.quantity_sent}
                  onChange={(e) => {
                    updateQuantitySentFromBatch(e, index, maxQuantity);
                  }}
                  onBlur={() => setErrors({})}
                  error={errors[index]}
                />
                {batches?.length > 0 && (
                  <div className='w-full'>
                    <SelectDropdown
                      label='Batch'
                      placeholder='Select batch'
                      value={'Batch' + ' ' + b?.batch_no}
                      setValue={(val) => {
                        updateSentItems(index, { batch_no: val.name.split(' ')[1], id: val.id });
                      }}
                      options={availableBatches.map((batch) => ({
                        ...batch,
                        name: `Batch ${batch.name}`
                      }))}
                    />
                    <p className='text-sm text-[#96999C] mt-2'>
                      <span className='text-[#0080E5]'> Maximum Quantity: </span> {maxQuantity}
                    </p>
                  </div>
                )}

                <button type='button' onClick={() => removeBatch(index)} className=''>
                  <DeleteIcon className='self-center text-red-5 cursor-pointer w-5 h-5' />
                </button>
              </div>
            );
          })
        )}
      </div>
      <div className='flex justify-end'>
        {batches.length === 0 ? (
          <p className='text-gray-3'>There is no available batch in the store</p>
        ) : availableBatches.length > 0 ? (
          <button onClick={addNewBatch} className='text-[#0080E5]'>
            + Add new batch
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default KitchenRequestItem;
